import { useCallback, useEffect, useMemo, useState } from "react";
import { Menu } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import NotificationBell from "src/common/components/NotificationBell/NotificationBell";
import Badge from "src/common/components/Badge/Badge";
import { ReleaseFeedEntry } from "src/model/model";
import { fetchReleaseFeed, getUnreadCount, markSeen } from "../api/releasesApi";

/**
 * Release-specific adapter that wires the generic <NotificationBell> shell to
 * the release feed API (Phase 5). It owns all release knowledge — the unread
 * count, the 3 latest entries, the surface chips and the copy — so the shell
 * stays source-agnostic.
 */

const surfaceChip: Record<ReleaseFeedEntry["surface"], { color: string; textColor: string }> = {
  web: { color: "bg-blue-100", textColor: "text-blue-700" },
  vr: { color: "bg-purple-100", textColor: "text-purple-700" },
  both: { color: "bg-green-100", textColor: "text-green-700" },
};

/**
 * Deep-link to an entry in the full timeline. A bare `/releases` lands on the
 * default "Tous" split view, where both columns render — so whatever the
 * entry's surface, the owning column(s) find the hash, scroll to the card and
 * flash its highlight (see ReleaseColumn's deep-link effect).
 */
const releaseLink = (entry: ReleaseFeedEntry) => `/releases#release-${entry.id}`;

const ReleaseBell = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language === "fr" ? fr : enUS;

  const [unreadCount, setUnreadCount] = useState(0);
  const [entries, setEntries] = useState<ReleaseFeedEntry[]>([]);

  // The newest entry of each surface is that surface's "current version".
  // Entries are DESC-sorted, so the first occurrence of a surface value is latest.
  const currentBySurface = useMemo(() => {
    const seen = new Set<ReleaseFeedEntry["surface"]>();
    const ids = new Set<number>();
    entries.forEach((entry) => {
      if (!seen.has(entry.surface)) {
        seen.add(entry.surface);
        ids.add(entry.id);
      }
    });
    return ids;
  }, [entries]);

  const refreshUnread = useCallback(async () => {
    try {
      const { count } = await getUnreadCount();
      setUnreadCount(count);
    } catch {
      // A feed-count hiccup must never break the dashboard header.
    }
  }, []);

  useEffect(() => {
    refreshUnread();
    fetchReleaseFeed({ limit: 3 })
      .then(setEntries)
      .catch(() => {
        // Non-fatal: render an empty popover rather than crash the header.
      });
  }, [refreshUnread]);

  // Opening the popover acknowledges everything that exists now: mark-seen sets
  // the server-side cursor to the global max id, then we refetch to clear the dot.
  const handleOpen = useCallback(() => {
    setUnreadCount(0); // optimistic
    markSeen()
      .then(refreshUnread)
      .catch(() => {
        // Roll back to the real count if the mark-seen call failed.
        refreshUnread();
      });
  }, [refreshUnread]);

  return (
    <NotificationBell
      title={t("components.releaseWidget.title")}
      unreadCount={unreadCount}
      onOpen={handleOpen}
      footer={
        <Menu.Item>
          {({ active }: { active: boolean }) => (
            <button
              type="button"
              onClick={() => navigate("/releases")}
              className={`w-full px-4 py-2 text-sm font-medium text-green-700 text-center ${
                active ? "bg-green-50" : ""
              }`}
            >
              {t("components.releaseWidget.seeAll")}
            </button>
          )}
        </Menu.Item>
      }
    >
      {entries.length === 0 ? (
        <p className="px-4 py-6 text-sm text-gray-500 text-center">
          {t("components.releaseWidget.empty")}
        </p>
      ) : (
        entries.map((entry) => (
          <Menu.Item key={entry.id}>
            {({ active }: { active: boolean }) => (
              <button
                type="button"
                onClick={() => navigate(releaseLink(entry))}
                className={`w-full text-left px-4 py-3 ${active ? "bg-gray-50" : ""}`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  {/* Left: surface + "current" dot + mandatory + version. Wraps
                      within its own column so the date never gets squeezed. */}
                  <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                    <Badge
                      color={surfaceChip[entry.surface].color}
                      textColor={surfaceChip[entry.surface].textColor}
                      text={t(`components.releaseWidget.surface.${entry.surface}`)}
                    />
                    {currentBySurface.has(entry.id) && (
                      <span
                        title={t("components.releaseWidget.current")}
                        aria-label={t("components.releaseWidget.current")}
                        className="inline-block h-2 w-2 shrink-0 rounded-full bg-green-500"
                      />
                    )}
                    {/* "Obligatoire" badge is intentionally omitted here to keep
                        the compact popover row tidy — it stays in the full
                        /releases view (see Releases.tsx). */}
                    {entry.version && (
                      <span className="text-xs text-gray-400">{entry.version}</span>
                    )}
                  </div>
                  {/* Right: date, pinned top-right on a single line. */}
                  {entry.published_at_user_feed && (
                    <span className="shrink-0 whitespace-nowrap text-xs text-gray-400 mt-1">
                      {format(new Date(entry.published_at_user_feed), "d MMM yyyy", {
                        locale,
                      })}
                    </span>
                  )}
                </div>
                <p className="text-sm font-medium text-black break-words">{entry.headline}</p>
              </button>
            )}
          </Menu.Item>
        ))
      )}
    </NotificationBell>
  );
};

export default ReleaseBell;
